/**
 * TalentsPanel — TAL-1
 *
 * First real panel inside the Settings shell.
 * Admin mode: shows Built/Wiring/Planned with launch readiness.
 * Tenant mode: shows user's capabilities with upgrade nudges.
 */

import React, { useState, useEffect } from 'react';
import { TalentRow, type TalentEntry } from './TalentRow';
import { LaunchReadinessPanel, type LaunchReadiness } from './LaunchReadinessPanel';
import { ConnectorCoverageGrid, type ConnectorCoverage } from './ConnectorCoverageGrid';
import { API_V1 } from '@/lib/env';
import { buildEngineHeaders } from '@/lib/auth';

interface TalentInventory {
    built: TalentEntry[];
    wiring: TalentEntry[];
    planned: TalentEntry[];
    summary: {
        total_built: number;
        total_wiring: number;
        total_planned: number;
        launch_critical_total: number;
        launch_critical_done: number;
        launch_readiness_pct: number;
    };
}

// Group entries by category
function groupByCategory(entries: TalentEntry[]): Record<string, TalentEntry[]> {
    const groups: Record<string, TalentEntry[]> = {};
    for (const entry of entries) {
        const cat = entry.category || 'other';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(entry);
    }
    return groups;
}

// Group entries by priority
function groupByPriority(entries: TalentEntry[]): Record<string, TalentEntry[]> {
    const groups: Record<string, TalentEntry[]> = {};
    const order = ['launch', 'month_1', 'quarter_1', 'someday'];
    for (const p of order) groups[p] = [];
    for (const entry of entries) {
        const pri = entry.priority || 'someday';
        if (!groups[pri]) groups[pri] = [];
        groups[pri].push(entry);
    }
    // Remove empties
    return Object.fromEntries(Object.entries(groups).filter(([, v]) => v.length > 0));
}

const PRIORITY_LABELS: Record<string, string> = {
    launch: 'LAUNCH',
    month_1: 'MONTH-1',
    quarter_1: 'Q1',
    someday: 'SOMEDAY',
};

const CATEGORY_LABELS: Record<string, string> = {
    connector: 'Connectors',
    skill: 'Skills',
    sink: 'Sinks',
    governance: 'Governance',
    cron: 'Cron',
    engine: 'Pipelines',
    marketplace_seed: 'Marketplace Seeds',
    other: 'Other',
};

interface TalentsPanelProps {
    mode: 'admin' | 'tenant';
}

export const TalentsPanel: React.FC<TalentsPanelProps> = ({ mode }) => {
    const [inventory, setInventory] = useState<TalentInventory | null>(null);
    const [launchReadiness, setLaunchReadiness] = useState<LaunchReadiness | null>(null);
    const [connectorCoverage, setConnectorCoverage] = useState<ConnectorCoverage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [builtExpanded, setBuiltExpanded] = useState(mode === 'tenant');
    const [wiringExpanded, setWiringExpanded] = useState(true);
    const [plannedExpanded, setPlannedExpanded] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

    useEffect(() => {
        fetchData();
    }, [mode]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const headers = buildEngineHeaders();

            const endpoint = mode === 'admin'
                ? `${API_V1}/talents/inventory/admin`
                : `${API_V1}/talents/inventory`;

            const resp = await fetch(endpoint, { headers });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data: TalentInventory = await resp.json();
            setInventory(data);

            // Fetch launch readiness + connector coverage in admin mode
            if (mode === 'admin') {
                const [lrResp, ccResp] = await Promise.all([
                    fetch(`${API_V1}/talents/launch-readiness`, { headers }),
                    fetch(`${API_V1}/talents/connector-coverage`, { headers }),
                ]);
                if (lrResp.ok) {
                    setLaunchReadiness(await lrResp.json());
                }
                if (ccResp.ok) {
                    setConnectorCoverage(await ccResp.json());
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load inventory');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 font-mono text-sm animate-pulse">
                    Loading inventory...
                </div>
            </div>
        );
    }

    if (error || !inventory) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-400 font-mono text-sm">
                    {error || 'Failed to load inventory'}
                </div>
            </div>
        );
    }

    const { summary } = inventory;

    // Apply category filter
    const filterEntries = (entries: TalentEntry[]) =>
        categoryFilter ? entries.filter(e => e.category === categoryFilter) : entries;

    if (mode === 'admin') {
        return <AdminView
            inventory={inventory}
            summary={summary}
            launchReadiness={launchReadiness}
            connectorCoverage={connectorCoverage}
            builtExpanded={builtExpanded}
            setBuiltExpanded={setBuiltExpanded}
            wiringExpanded={wiringExpanded}
            setWiringExpanded={setWiringExpanded}
            plannedExpanded={plannedExpanded}
            setPlannedExpanded={setPlannedExpanded}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            filterEntries={filterEntries}
        />;
    }

    return <TenantView inventory={inventory} summary={summary} filterEntries={filterEntries} />;
};

// --- Admin View ---

interface AdminViewProps {
    inventory: TalentInventory;
    summary: TalentInventory['summary'];
    launchReadiness: LaunchReadiness | null;
    connectorCoverage: ConnectorCoverage | null;
    builtExpanded: boolean;
    setBuiltExpanded: (v: boolean) => void;
    wiringExpanded: boolean;
    setWiringExpanded: (v: boolean) => void;
    plannedExpanded: boolean;
    setPlannedExpanded: (v: boolean) => void;
    categoryFilter: string | null;
    setCategoryFilter: (v: string | null) => void;
    filterEntries: (entries: TalentEntry[]) => TalentEntry[];
}

const AdminView: React.FC<AdminViewProps> = ({
    inventory, summary, launchReadiness, connectorCoverage,
    builtExpanded, setBuiltExpanded,
    wiringExpanded, setWiringExpanded,
    plannedExpanded, setPlannedExpanded,
    categoryFilter, setCategoryFilter,
    filterEntries,
}) => {
    const builtByCategory = groupByCategory(filterEntries(inventory.built));
    const wiringByPriority = groupByPriority(filterEntries(inventory.wiring));
    const plannedByPriority = groupByPriority(filterEntries(inventory.planned));

    return (
        <div className="space-y-4">
            {/* Header bar */}
            <div className="flex items-center justify-between">
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
                    Talents :: Inventory
                </h3>
                <div className="flex items-center gap-4 text-[11px] font-mono">
                    <span className="text-[#39FF14]">Built: {summary.total_built}</span>
                    <span className="text-amber-400">Wiring: {summary.total_wiring}</span>
                    <span className="text-blue-400">Planned: {summary.total_planned}</span>
                </div>
            </div>

            {/* Launch readiness progress bar */}
            <div className="bg-gray-900/40 border border-gray-800 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono text-gray-500 uppercase">Launch Ready</span>
                    <span className="text-[11px] font-mono text-gray-400">
                        {summary.launch_critical_done}/{summary.launch_critical_total}
                    </span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                        className="bg-[#39FF14] h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(summary.launch_readiness_pct, 100)}%` }}
                    />
                </div>
                <span className="text-[10px] font-mono text-gray-500 mt-1 block">
                    {summary.launch_readiness_pct}%
                </span>
            </div>

            {/* Two-column layout: lists on left, readiness + coverage on right */}
            <div className="grid grid-cols-[1fr_340px] gap-4">
                {/* Left column: category lists */}
                <div className="space-y-3">
                    {/* Category filter chips */}
                    <div className="flex gap-1 flex-wrap">
                        <FilterChip label="All" active={!categoryFilter} onClick={() => setCategoryFilter(null)} />
                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                            <FilterChip
                                key={key}
                                label={label}
                                active={categoryFilter === key}
                                onClick={() => setCategoryFilter(key)}
                            />
                        ))}
                    </div>

                    {/* BUILT section */}
                    <CollapsibleSection
                        title={`BUILT (${filterEntries(inventory.built).length})`}
                        expanded={builtExpanded}
                        onToggle={() => setBuiltExpanded(!builtExpanded)}
                        color="text-[#39FF14]"
                    >
                        {Object.entries(builtByCategory).map(([cat, entries]) => (
                            <div key={cat} className="mb-2">
                                <div className="text-[10px] font-mono text-gray-500 uppercase mb-1 pl-2">
                                    {CATEGORY_LABELS[cat] || cat} ({entries.length})
                                </div>
                                {entries.map(e => (
                                    <TalentRow key={e.slug} entry={e} mode="admin" />
                                ))}
                            </div>
                        ))}
                    </CollapsibleSection>

                    {/* WIRING section */}
                    <CollapsibleSection
                        title={`WIRING (${filterEntries(inventory.wiring).length})`}
                        expanded={wiringExpanded}
                        onToggle={() => setWiringExpanded(!wiringExpanded)}
                        color="text-amber-400"
                    >
                        {Object.entries(wiringByPriority).map(([pri, entries]) => (
                            <div key={pri} className="mb-2">
                                <div className="text-[10px] font-mono text-gray-500 uppercase mb-1 pl-2">
                                    {PRIORITY_LABELS[pri] || pri} ({entries.length})
                                </div>
                                {entries.map(e => (
                                    <TalentRow key={e.slug} entry={e} mode="admin" />
                                ))}
                            </div>
                        ))}
                    </CollapsibleSection>

                    {/* PLANNED section */}
                    <CollapsibleSection
                        title={`PLANNED (${filterEntries(inventory.planned).length})`}
                        expanded={plannedExpanded}
                        onToggle={() => setPlannedExpanded(!plannedExpanded)}
                        color="text-blue-400"
                    >
                        {Object.entries(plannedByPriority).map(([pri, entries]) => (
                            <div key={pri} className="mb-2">
                                <div className="text-[10px] font-mono text-gray-500 uppercase mb-1 pl-2">
                                    {PRIORITY_LABELS[pri] || pri} ({entries.length})
                                </div>
                                {entries.map(e => (
                                    <TalentRow key={e.slug} entry={e} mode="admin" />
                                ))}
                            </div>
                        ))}
                    </CollapsibleSection>
                </div>

                {/* Right column: Launch Readiness + Connector Coverage */}
                <div className="space-y-4">
                    {launchReadiness && (
                        <LaunchReadinessPanel readiness={launchReadiness} />
                    )}
                    <ConnectorCoverageGrid coverage={connectorCoverage ?? undefined} />
                </div>
            </div>
        </div>
    );
};

// --- Tenant View ---

interface TenantViewProps {
    inventory: TalentInventory;
    summary: TalentInventory['summary'];
    filterEntries: (entries: TalentEntry[]) => TalentEntry[];
}

const TenantView: React.FC<TenantViewProps> = ({ inventory, summary }) => {
    const byCategory = groupByCategory(inventory.built);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
                    My Capabilities
                </h3>
                <span className="text-[10px] font-mono text-gray-500">
                    {summary.total_built} active
                </span>
            </div>

            {/* Grouped by category */}
            {Object.entries(byCategory).map(([cat, entries]) => (
                <div key={cat}>
                    <div className="text-[10px] font-mono text-gray-500 uppercase mb-2 tracking-wider">
                        {CATEGORY_LABELS[cat] || cat}
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                        {entries.map(e => (
                            <TalentRow key={e.slug} entry={e} mode="tenant" />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

// --- Shared sub-components ---

const CollapsibleSection: React.FC<{
    title: string;
    expanded: boolean;
    onToggle: () => void;
    color: string;
    children: React.ReactNode;
}> = ({ title, expanded, onToggle, color, children }) => (
    <div className="border border-gray-800 rounded bg-gray-900/20">
        <button
            onClick={onToggle}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-mono ${color} hover:bg-gray-800/30 transition-colors`}
        >
            <span className="text-[10px]">{expanded ? '▼' : '▸'}</span>
            {title}
        </button>
        {expanded && <div className="px-3 pb-3">{children}</div>}
    </div>
);

const FilterChip: React.FC<{
    label: string;
    active: boolean;
    onClick: () => void;
}> = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`px-2 py-0.5 text-[10px] font-mono rounded transition-colors
            ${active
                ? 'bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30'
                : 'bg-gray-900 text-gray-500 border border-gray-800 hover:text-gray-300'
            }`}
    >
        {label}
    </button>
);
